import { complianceEndpoints } from './handlers/exampleCompliance';
import { complianceDocumentEndpoints } from './handlers/compliance';
import { complianceDocGeneratorEndpoints } from './handlers/complianceDocGenerator';
import { authEndpoints } from './handlers/auth';
import { imageEndpoints } from './handlers/images';
import { accessControlEndpoints } from './handlers/accessControl';
import { taskEndpoints } from './handlers/task';
import { genericEndpoints, certificateSupplierMetaEndpoints } from './handlers/genericData';
import { suppliersEndpoints } from './handlers/suppliers';
import { viewsEndpoints } from './handlers/views';
import { t } from '@lingui/macro';
import { VITE_MOCK_ONLY } from 'config';
import type { Server } from 'miragejs';
import { facilitiesEndpoints } from './handlers/facilities';
import {
  supplierAccessEndpoints,
  supplierGuidedOnboardingEndpoints,
} from './handlers/supplierAccess';
import { purchaseOrdersEndpoints } from './handlers/purchaseOrders';
import { purchaseOrdersMappingEndpoints } from './handlers/purchaseOrdersMapping';
import { productCertificatesMappingEndpoints } from './handlers/productCertificatesMapping';
import { itemsEndpoints } from './handlers/items';
import { organizationMigrationEndpoints } from './handlers/organizationMigration';
import { migrationVerificationReportEndpoints } from './handlers/migrationVerificationReport';
import { stylesEndpoints } from './handlers/styles';
import { stylesV2Endpoints } from './handlers/stylesV2';
import { itemsV2Endpoints } from './handlers/itemsV2';
import { sampleRequestsV2Endpoints } from './handlers/sampleRequestsV2';
import { organizationIntegrationAccessEndpoints } from './handlers/organizationIntegrationAccess';
import { registerBrandCompaniesEndpoints } from './handlers/brandCompanies';
import { supplierInviteConnectionsEndpoints } from './handlers/supplierInviteConnections';
import { pendingConnectionsEndpoints } from './handlers/pendingConnections';
import { legacyOrgAdminsEndpoints } from './handlers/legacyOrgAdmins';
import { supplierMergeRequestsEndpoints } from './handlers/supplierMergeRequests';
import { fileUploadEndpoints } from './handlers/fileUpload';
import { seedFacilityCertificatesFixture } from 'mocks/data/facilityCertificatesFixture';
import { seedFacilityCertificatesCrudFixture } from 'mocks/data/facilityCertificatesCrudFixture';
import { HTTP_RESPONSE_CODES } from '../constants';

const enabledEndpoints = (server: Server) => {
  complianceEndpoints(server);
  imageEndpoints(server);
  accessControlEndpoints(server);
};

const disabledEndpoints = (server: Server) => {
  // CDG mocks are VITE_MOCK_ONLY (vitest/playwright): the dev browser hits the
  // live BE for the compliance-document + doc-generator surfaces (DL-61574).
  complianceDocumentEndpoints(server);
  complianceDocGeneratorEndpoints(server);
  authEndpoints(server);
  suppliersEndpoints(server);
  registerBrandCompaniesEndpoints(server);
  // Brand–Supplier pending connections (dl-61067): VITE_MOCK_ONLY only so
  // live DOMAIN is not shadowed when Mirage starts in the normal browser.
  pendingConnectionsEndpoints(server);
  taskEndpoints(server);
  facilitiesEndpoints(server);
  genericEndpoints(server);
  // Certificate modal lookup GETs (scm-certificate-standards / -institutes): the handler was
  // defined in genericData but never registered on the server (DL-63905) — the create-certificate
  // modal's Standard/Institute dropdowns had no mock and failed with a network error in mock mode.
  certificateSupplierMetaEndpoints(server);
  purchaseOrdersEndpoints(server);
  purchaseOrdersMappingEndpoints(server);
  // purchaseOrdersReviewEndpoints is registered internally by purchaseOrdersEndpoints (with the
  // real getPurchaseOrdersData getter it now requires) — do not also register it here, it would
  // just be a redundant duplicate registration with no access to that getter.
  // POST .../facility-process-certificates (assign/remove product certificates on the Step 4 grid,
  // `productCertificatesMapSuppliers` tab) — previously defined but never registered on the server,
  // so the endpoint 404'd in every mock-mode run (DL-63493 epic prerequisite 3 finding). Purely
  // additive: this is the only place that route now exists.
  productCertificatesMappingEndpoints(server);
  supplierAccessEndpoints(server);
  viewsEndpoints(server);
  itemsEndpoints(server);
  organizationMigrationEndpoints(server);
  migrationVerificationReportEndpoints(server);
  stylesEndpoints(server);
  // New-model Styles/Items/Sample-requests v2 prototype (DL-62519) — mock-only, additive.
  stylesV2Endpoints(server);
  itemsV2Endpoints(server);
  sampleRequestsV2Endpoints(server);
  organizationIntegrationAccessEndpoints(server);
  // Invite supplier (dl-61766) — VITE_MOCK_ONLY only, never enabledEndpoints.
  supplierInviteConnectionsEndpoints(server);
  supplierGuidedOnboardingEndpoints(server);
  legacyOrgAdminsEndpoints(server);
  supplierMergeRequestsEndpoints(server);
  // Generic legacy file-upload endpoint (DL-63905) — only the facility-certificate upload flow
  // consumes it today; VITE_MOCK_ONLY only, same placement as the other supplier/facility mocks.
  fileUploadEndpoints(server);

  // Deterministic, additive seed for the facility-certificate "inactive row fade" epic
  // (dl-63961). Mock-only; adds one supplier-portal sub-supplier (org 63) with fixed certificates.
  seedFacilityCertificatesFixture();
  // Additive seed for the Supplier Portal add/edit/delete certificate epic (dl-63719): owned
  // facilities (org-scoped route) + a dedicated CRUD sub-supplier (regression route). Runs after
  // the fade fixture so both coexist.
  seedFacilityCertificatesCrudFixture();
};

export const endpoints = (server: Server) => {
  enabledEndpoints(server);
  if (VITE_MOCK_ONLY) {
    disabledEndpoints(server);
  }
};

export { HTTP_RESPONSE_CODES };

export const ERROR_MESSAGES = {
  [HTTP_RESPONSE_CODES.NO_CHANGE]: t`204 No change`,
  [HTTP_RESPONSE_CODES.BAD_INPUT]: t`400 Bad input`,
  [HTTP_RESPONSE_CODES.UNAUTHORIZED]: t`'401 User is not authorised`,
  [HTTP_RESPONSE_CODES.BAD_INPUT]: t`403 Action not allowed`,
  [HTTP_RESPONSE_CODES.ENTITY_NOT_FOUND]: t`404 Entity not found`,
  [HTTP_RESPONSE_CODES.SERVER_ERROR]: t`500 Server error`,
};
